import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaw,
  faClock,
  faBone,
  faShoppingBag,
  faCouch,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { useRetailSection } from '../../../hooks/useRetailSection';

const MAIN_IMAGE = 'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.14.webp';

const GALLERY_IMAGES = [
  'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.34.webp',
  'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.45.webp',
  'https://BelvoirCastle.b-cdn.net/DogShop/Screenshot%202026-08-25%20at%2013.24.55.webp'
];

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Hero = styled.section`
  position: relative;
  min-height: calc(100dvh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #fff;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(16, 29, 64, 0.9) 0%,
    rgba(16, 29, 64, 0.5) 34%,
    rgba(16, 29, 64, 0.12) 64%,
    rgba(16, 29, 64, 0.2) 100%
  );
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: clamp(2.5rem, 6vw, 4.5rem) 1.5rem clamp(3rem, 7vw, 4.75rem);
  text-align: center;
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.85rem;
  max-width: 14ch;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(3.4rem, 8vw, 6rem);
  font-weight: 500;
  line-height: 0.95;
  color: #fff;
  text-shadow:
    0 1px 1px rgba(0, 0, 0, 0.85),
    0 4px 10px rgba(0, 0, 0, 0.7),
    0 14px 28px rgba(0, 0, 0, 0.55);
`;

const HeroSubtitle = styled.p`
  margin: 0 0 2.35rem;
  max-width: 36rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(0.69rem, 1.2vw, 0.8125rem);
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
  text-shadow:
    0 1px 1px rgba(0, 0, 0, 0.85),
    0 4px 10px rgba(0, 0, 0, 0.7),
    0 14px 28px rgba(0, 0, 0, 0.55);
`;

const ExploreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.1rem;
  padding: 0.9rem 1.7rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent;
  color: #fff;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ContentContainer = styled.div`
  scroll-margin-top: 120px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const BodyText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 1.5rem;
  text-align: center;
`;

const CurrentTimeDisplay = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 3rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 500;
`;

const ShopCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto 4rem;
  position: relative;
`;

const CardImage = styled.div`
  height: 350px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const CardContent = styled.div`
  padding: 2rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 1rem 0;
`;

const StatusIndicator = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  margin-right: 8px;
`;

const StatusText = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  display: flex;
  align-items: center;
`;

const TimeInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const DayLabel = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
`;

const Hours = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.6rem;
  font-weight: 500;
`;

const ShopDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.secondary};
`;

const AddressLine = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin: 0.5rem 0 0;
  
  svg {
    margin-right: 0.5rem;
    color: ${props => props.theme.colors.accent};
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => props.isOpen ? 'rgba(76, 175, 80, 0.85)' : 'rgba(244, 67, 54, 0.85)'};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 2rem 0 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  
  svg {
    color: white;
    font-size: 32px;
  }
`;

const FeatureTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
`;

const StockList = styled.ul`
  max-width: 700px;
  margin: 0 auto 2rem;
  padding: 0;
  list-style: none;
`;

const StockItem = styled.li`
  font-size: 1.15rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  text-align: center;
  
  &:last-child {
    border-bottom: none;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin: 0 auto 4rem;
  max-width: 1000px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const BelvoirPetShop = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useRetailSection();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });

  const openingHours = {
    days: 'Daily',
    times: '9:00 - 16:00'
  };

  const isShopOpen = () => {
    const timeRange = openingHours.times.split('-').map(t => t.trim());
    if (timeRange.length !== 2) return false;

    const [openHour, openMinute] = timeRange[0].split(':').map(Number);
    const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);

    const openTime = new Date();
    openTime.setHours(openHour, openMinute || 0, 0);

    const closeTime = new Date();
    closeTime.setHours(closeHour, closeMinute || 0, 0);

    return currentTime >= openTime && currentTime <= closeTime;
  };

  return (
    <PageContainer>
      <Hero>
        <HeroImage src={MAIN_IMAGE} alt="" />
        <HeroOverlay />
        <HeroInner>
          <HeroTitle>Belvoir Pet Shop</HeroTitle>
          <HeroSubtitle>A boutique dedicated entirely to dogs, at Belvoir Retail Village</HeroSubtitle>
          <ExploreButton
            href="#shop"
            onClick={(event) => {
              event.preventDefault();
              const target = document.getElementById('shop');
              if (!target) return;
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
            }}
          >
            Explore the shop
          </ExploreButton>
        </HeroInner>
      </Hero>

      <ContentContainer id="shop">
        <SectionTitle>Belvoir Dog Shop</SectionTitle>
        <IntroText>
          Belvoir Dog Shop is a boutique dedicated entirely to dogs, set within Belvoir Retail Village at the foot of the castle. Dogs have always been part of life on the Belvoir Estate, and this shop was created to celebrate that, offering a thoughtful, elevated space where every dog is welcome, whether they&apos;ve just finished a long walk around the grounds or are popping in for a coffee with their owner.
        </IntroText>

        <CurrentTimeDisplay>
          Current Time: {formattedTime} on {currentDay}
        </CurrentTimeDisplay>

        <ShopCard>
          <CardImage image={MAIN_IMAGE}>
            <StatusBadge isOpen={isShopOpen()}>
              <FontAwesomeIcon icon={faClock} />
              {isShopOpen() ? 'Open Now' : 'Closed'}
            </StatusBadge>
          </CardImage>
          <CardContent>
            <CardTitle>Belvoir Dog Shop at Belvoir Castle</CardTitle>
            <StatusContainer>
              <StatusText isOpen={isShopOpen()}>
                <StatusIndicator isOpen={isShopOpen()} />
                {isShopOpen() ? 'Open Now' : 'Closed'}
              </StatusText>
            </StatusContainer>
            <TimeInfo>
              <DayLabel>{openingHours.days}</DayLabel>
              <Hours>{openingHours.times}</Hours>
            </TimeInfo>
            <ShopDescription>
              Belvoir Dog Shop is located within Belvoir Retail Village, a dog-friendly shop set amongst one of the restored estate buildings at the foot of Belvoir Castle. Open seven days a week, the Village sits in the heart of the Leicestershire countryside — a popular spot for dog walkers, with routes including the three-mile circular Duke&apos;s Walk through Capability Brown-designed parkland.
            </ShopDescription>
            <ShopDescription>
              Dogs are also welcome in many other parts of the estate, including the gardens, parkland, and outdoor dining areas at Vale View Cafe and Belvoir Bistro.
            </ShopDescription>
            <AddressLine>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              Belvoir Retail Village, Belvoir Castle, Grantham, Leicestershire
            </AddressLine>
          </CardContent>
        </ShopCard>

        <SectionTitle>What We Stock</SectionTitle>
        <IntroText>
          Spread across two floors, Belvoir Barkery brings together a carefully curated range of premium products, chosen for their craftsmanship, durability and design.
        </IntroText>

        <StockList>
          <StockItem>Premium collars and leads</StockItem>
          <StockItem>Designer dog apparel</StockItem>
          <StockItem>Plush beds and stylish carriers</StockItem>
          <StockItem>Gourmet and natural treats</StockItem>
          <StockItem>Toys and accessories</StockItem>
        </StockList>

        <BodyText>
          Our shelves feature trusted names including Norfolk Doggy Deli, The Foggy Dog, Lambwolf Collective and Ralph &amp; Co, alongside other high-end canine brands.
        </BodyText>

        <GalleryGrid>
          {GALLERY_IMAGES.map((src) => (
            <GalleryImage key={src} src={src} alt="Belvoir Dog Shop interior" />
          ))}
        </GalleryGrid>

        <SectionTitle>A Shop for Every Dog</SectionTitle>
        <FeaturesGrid>
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faPaw} />
            </IconWrapper>
            <FeatureTitle>Every Dog Welcome</FeatureTitle>
            <FeatureDescription>
              A thoughtful, elevated space for dogs who have just finished a walk around the grounds, or are popping in with their owner.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faBone} />
            </IconWrapper>
            <FeatureTitle>Gourmet Treats</FeatureTitle>
            <FeatureDescription>
              Gourmet and natural treats from trusted names, chosen to match the quality you would expect from the estate.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faShoppingBag} />
            </IconWrapper>
            <FeatureTitle>Premium Brands</FeatureTitle>
            <FeatureDescription>
              Collars, leads, apparel and accessories from Norfolk Doggy Deli, The Foggy Dog, Lambwolf Collective, Ralph &amp; Co and more.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faCouch} />
            </IconWrapper>
            <FeatureTitle>Two Floors</FeatureTitle>
            <FeatureDescription>
              Beds, carriers, toys and grooming finds spread across two floors of a restored estate building at the foot of the castle.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default BelvoirPetShop;
